import { Store } from 'store/storeTypes';
import { MINUTES_PER_DAY, SLOT_STEP_MINUTES } from './workingHoursConstants';
import { daySlotsFit, getCarryInMinutesForDay, withCarryInReserve } from './utils/slotOverlapUtils';

export const selectWorkingHoursSite = (state: Store) => state.workingHours.selectedSite;
export const selectWorkingHoursSites = (state: Store) => state.workingHours.selectedSites;
export const selectWorkingHoursIsMixedSites = (state: Store) => state.workingHours.isMixedSites;
export const selectWorkingHoursApplyMode = (state: Store) => state.workingHours.applyMode;
export const selectWorkingHoursStartDate = (state: Store) => state.workingHours.startDate;
export const selectWorkingHoursDays = (state: Store) => state.workingHours.days;
export const selectWorkingHoursConfigurationLoadId = (state: Store) =>
  state.workingHours.configurationLoadId;
export const selectWorkingHoursSystemGroup = (state: Store) =>
  state.workingHours.selectedSystemGroup;
export const selectWorkingHoursSystemGroupTypes = (state: Store) =>
  state.workingHours.selectedSystemGroupTypes;
export const selectWorkingHoursEditor = (state: Store) => state.workingHours.editor;
export const selectWorkingHoursIsMixedSystemGroupTypes = (state: Store) =>
  state.workingHours.isMixedSystemGroupTypes;
export const selectWorkingHoursViewingSystemGroupType = (state: Store) =>
  state.workingHours.viewingSystemGroupType;

export type WorkingHoursEditorValidation = {
  finalEndMinutes: number;
  duration: number;
  // Blocks Apply entirely — the draft itself is invalid (not a scheduling conflict).
  conflictMessage: string | null;
  // Days (from editor.applyDays) whose existing slots would overlap this frame.
  // Apply still proceeds for the rest; these are left untouched and reported to the user.
  conflictingDayIds: string[];
};

export const selectWorkingHoursEditorValidation = (
  state: Store
): WorkingHoursEditorValidation | null => {
  const editor = state.workingHours.editor;
  if (!editor) return null;

  const { dayId: homeDayId, slotId, startMinutes, endMinutes, overnight, applyDays } = editor;
  const finalEndMinutes = overnight ? endMinutes % MINUTES_PER_DAY : endMinutes;
  const rawSpan = overnight
    ? (finalEndMinutes <= startMinutes ? finalEndMinutes + MINUTES_PER_DAY : finalEndMinutes) -
      startMinutes
    : finalEndMinutes - startMinutes;
  const duration = Math.max(SLOT_STEP_MINUTES, rawSpan);

  const conflictMessage =
    !overnight && finalEndMinutes <= startMinutes ? 'End time must be after start time.' : null;

  const candidate = { startMinutes, endMinutes: finalEndMinutes };
  const conflictingDayIds = conflictMessage
    ? []
    : applyDays.filter(applyDayId => {
        const day = state.workingHours.days.find(d => d.id === applyDayId);
        if (!day) return false;
        const excludeSlotId = applyDayId === homeDayId ? slotId : null;
        const reserve = getCarryInMinutesForDay(state.workingHours.days, applyDayId);
        return !daySlotsFit(withCarryInReserve(day.slots, reserve), excludeSlotId, candidate);
      });

  return { finalEndMinutes, duration, conflictMessage, conflictingDayIds };
};
